import { Dispatch, SetStateAction } from 'react'

import { FirebaseError } from 'firebase/app'
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ListResult,
  ref,
  StorageReference,
} from 'firebase/storage'

import { handleErrors } from '../../utilFunctions/firebaseErrors'
import { storage } from '../firebaseConfig'
import { getUserDownloadURLWithLegacyFallback } from '../storageFallback'
import { storagePaths } from '../storagePaths'

export type TrackAudioFile = { title: string; url: string }

export type TrackFolder = {
  tracks: TrackAudioFile[]
  folders: Record<string, TrackFolder>
}

/** Árbol dinámico de carpetas y audios bajo `tracks/`. */
export type TracksAudioIndex = TrackFolder

const MAX_TRACK_TREE_DEPTH = 8
const MAX_TRACK_TREE_FOLDERS = 500

type TrackTreeBudget = {
  visitedFolders: number
}

async function mapAudioFiles(
  items: ListResult['items'],
): Promise<TrackAudioFile[]> {
  return Promise.all(
    items.map(async (file) => ({
      title: file.name,
      url: await getDownloadURL(file),
    })),
  )
}

async function listTrackTree(
  folderRef: StorageReference,
  depth: number,
  budget: TrackTreeBudget,
): Promise<TrackFolder> {
  if (depth > MAX_TRACK_TREE_DEPTH) {
    throw new Error(
      `La jerarquía de tracks supera ${MAX_TRACK_TREE_DEPTH} niveles.`,
    )
  }
  if (budget.visitedFolders >= MAX_TRACK_TREE_FOLDERS) {
    throw new Error(
      `La jerarquía de tracks supera ${MAX_TRACK_TREE_FOLDERS} carpetas.`,
    )
  }

  budget.visitedFolders += 1
  const result = await listAll(folderRef)
  const tracks = await mapAudioFiles(result.items)
  const folderEntries = await Promise.all(
    result.prefixes.map(async (folder) => {
      return [
        folder.name,
        await listTrackTree(folder, depth + 1, budget),
      ] as const
    }),
  )

  return {
    tracks,
    folders: Object.fromEntries(folderEntries),
  }
}

/**
 * Construye la jerarquía disponible bajo `tracks/` directamente desde Storage.
 * Los límites evitan recorridos accidentales o ilimitados en el plan Blaze.
 */
export async function buildTracksAudioIndex(): Promise<TracksAudioIndex> {
  return listTrackTree(ref(storage, storagePaths.tracksRoot), 0, {
    visitedFolders: 0,
  })
}

/** Índice legacy (carpetas raíz antiguas) para bitácoras ya guardadas. */
export async function buildLegacyExerciseAudioIndex(): Promise<
  Record<string, { title: string; url: string }[]>[] | null
> {
  try {
    const larFolderRef = ref(
      storage,
      storagePaths.legacyExerciseCatalog('estimulación-laríngea'),
    )
    const resFolderRef = ref(
      storage,
      storagePaths.legacyExerciseCatalog('trabajo-respiratorio'),
    )
    const resonanFolderRef = ref(
      storage,
      storagePaths.legacyExerciseCatalog('trabajo-resonancial'),
    )

    const [_larFiles, _resFiles, _resonanFiles] = await Promise.all([
      listAll(larFolderRef),
      listAll(resFolderRef),
      listAll(resonanFolderRef),
    ])

    const [larFiles, resFiles, resonanFiles] = await Promise.all([
      getFileByCategory(_larFiles),
      getFileByCategory(_resFiles),
      getFileByCategory(_resonanFiles),
    ])

    return [larFiles, resFiles, resonanFiles]
  } catch (err) {
    console.warn('Legacy exercise audio index unavailable:', err)
    return null
  }
}

export const listAllStorage = async (folderRef: StorageReference) => {
  listAll(folderRef)
    .then((res) => {
      const files = res.items
      const promises = files.map((itemRef) => {
        // Obtén el enlace de descarga de cada archivo
        return getDownloadURL(itemRef).then((downloadURL) => {
          return {
            name: itemRef.name,
            downloadLink: downloadURL,
          }
        })
      })
      return Promise.all(promises)
    })
    .then((results) => {
      return results
    })
    .catch((error) => {
      return error
    })
}

export const getFileByCategory = async (files: ListResult) => {
  const filesByType: Record<string, { title: string; url: string }[]> = {}

  for (const folder of files.prefixes) {
    const gender = folder.name
    const folderFiles = await listAll(folder)

    const mapped = await Promise.all(
      folderFiles.items.map(async (file) => ({
        title: file.name,
        url: await getDownloadURL(file),
      })),
    )

    filesByType[gender] = mapped
  }

  return filesByType
}

// NO SE USA
export const downloadFile = async (
  fileName: string,
  userId: string,
  setError?: Dispatch<SetStateAction<string | null>>,
) => {
  try {
    const { value: downloadUrl } = await getUserDownloadURLWithLegacyFallback(
      storage,
      storagePaths.userFile(userId, fileName),
      storagePaths.legacyUserFile(userId, fileName),
    )
    return downloadUrl
    // TODO: replace lines 114 and 115 with
    // const url = await guardedCall(() => getDownloadURL(fileRef))
    // return url
  } catch (error) {
    console.error('Error al descargar el archivo:', error)
    setError && `Error al descargar el archivo: ${error}`
  }
}

export const removeFile = async (
  refId: string,
  setError?: Dispatch<SetStateAction<string | null>>,
) => {
  try {
    const imgRef = ref(storage, refId)
    await deleteObject(imgRef)
  } catch (error) {
    const { code, message } = error as FirebaseError
    const friendly = handleErrors(code)
    console.error('Error al eliminar el archivo:', error)
    setError && setError(friendly || message)
  }
}
